import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { Router } from '@angular/router';
import { CommonBindingDataService } from '../../services/common-binding-data.service';
import { RestApiService } from '../../services/rest-api.service';
import { UiService } from '../../services/ui.service';
import { formatTimeCell, getDate } from '../../utility-functions/grid';

@Component({
  selector: 'app-passenger-day-bookings',
  templateUrl: './passenger-day-bookings.component.html',
  styleUrls: ['./passenger-day-bookings.component.scss']
})
export class PassengerDayBookingsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() passengerId;
  @Input() priorBookingDateStart;
  @Input() priorBookingDateEnd;

  generalBookingsData: any[] = [];
  dedicatedBookingsData: any[] = [];
  totalGeneralCount = 0;
  totalDedicatedCount = 0;
  totalJourneys = 0;
  isLoading = false;
  url = '';

  generalTabHeader = 'General';
  dedicatedTabHeader = 'Dedicated';

  labelBookingId: string;
  labelPickupTime: string;
  labelPickupDropOff: string;
  labelDriver: string;
  labelStatus: string;
  labelDuration: string;
  labelPassenger: string;
  labelPickupDetails: string;
  labelDriverVehicleInfo: string;
  labelNoRecords: string;

  pastDedicatedBookingStatus = AppSettings.PAST_DEDICATED_BOOKING_STATUS;

  private _broadSub: any;
  eventIdParam: any;

  constructor(
    private router: Router,
    private commonBindingDataService: CommonBindingDataService,
    private restApiService: RestApiService,
    private uiService: UiService,
  ) {
    this.labelBookingId = this.commonBindingDataService.getLabel('label_booking_ID');
    this.labelPickupTime = this.commonBindingDataService.getLabel('label_pickup_at');
    this.labelPickupDropOff = this.commonBindingDataService.getLabel('grid_pickup_drop_off');
    this.labelDriver = this.commonBindingDataService.getLabel('label_driver');
    this.labelStatus = this.commonBindingDataService.getLabel('label_status');
    this.labelDuration = this.commonBindingDataService.getLabel('label_duration');
    this.labelPassenger = this.commonBindingDataService.getLabel('grid_passenger');
    this.labelPickupDetails = this.commonBindingDataService.getLabel('label_pickup_details');
    this.labelDriverVehicleInfo = this.commonBindingDataService.getLabel('label_vehicle_idriver_info');
    this.labelNoRecords = this.commonBindingDataService.getLabel('label_no_records_found');
    this.commonBindingDataService.eventId.subscribe(result => {
      this.eventIdParam = result;
    });

    if (!this.eventIdParam) {
      let tempEventId = this.commonBindingDataService.changedEventId;
      if (tempEventId) {
        this.eventIdParam = tempEventId;
      }
    }
  }

  ngOnChanges() {
    if (this.passengerId) {
      this.priorBookingDateStart = this.priorBookingDateStart ?? new Date().setHours(0, 0, 0, 0);
      this.priorBookingDateEnd = this.priorBookingDateEnd ?? new Date().setHours(23, 59, 59, 59);
      this.url = `/secure/dedicated-bookings/passenger/${this.passengerId}/journey-history?eventId=${this.eventIdParam}`;
      this.loadJourneyHistory();
    }
  }

  ngOnInit() {
    this.priorBookingDateStart = this.priorBookingDateStart ?? new Date().setHours(0, 0, 0, 0);
    this.priorBookingDateEnd = this.priorBookingDateEnd ?? new Date().setHours(23, 59, 59, 59);
    this.commonBindingDataService.bookingTabCountChange.next(true);

    this._broadSub = this.commonBindingDataService.bookingGridRefresh.subscribe((result) => {
      if (result === AppSettings.SUB_BOOKINMG_SERVICE_TYPES_LIST.INBOUND) {
        this.loadJourneyHistory();
      }
    });

    if (this.passengerId) {
      this.loadJourneyHistory();
    }
  }

  loadJourneyHistory() {
    if (!this.passengerId) {
      return;
    }

    this.isLoading = true;
    if (!this.url) {
      this.url = `/secure/dedicated-bookings/passenger/${this.passengerId}/journey-history`;
    }

    this.restApiService.get(this.url, 'page-center').subscribe(
      (response: any) => {
        this.generalBookingsData = response?.tripHistory?.data ?? [];
        this.dedicatedBookingsData = response?.dedicatedBookingHistory?.data ?? [];
        this.totalGeneralCount = response?.tripHistory?.count ?? this.generalBookingsData.length;
        this.totalDedicatedCount = response?.dedicatedBookingHistory?.count ?? this.dedicatedBookingsData.length;
        this.totalJourneys = response?.totalJourneys ?? 0;
        this.updateTabHeaders();
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        this.generalBookingsData = [];
        this.dedicatedBookingsData = [];
        this.totalGeneralCount = 0;
        this.totalDedicatedCount = 0;
        this.updateTabHeaders();
        if (error?.general?.[0]?.message) {
          this.uiService.showMessage(error.general[0].message, AppSettings.MESSAGE_TYPE.ERROR);
        }
      }
    );
  }

  private updateTabHeaders() {
    this.generalTabHeader = `General (${this.totalGeneralCount})`;
    this.dedicatedTabHeader = `Dedicated (${this.totalDedicatedCount})`;
  }

  renderCell(cellFn: (row: any) => HTMLElement, row: any): string {
    return cellFn.call(this, row)?.innerHTML ?? '';
  }

  getGeneralBookingIdCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const tripId = params.tripId || 'NA';
    eDiv.innerHTML = `<div class="booking-grid-table"><div class="passenger-name font-bold">${tripId}</div></div>`;
    return eDiv;
  };

  getGeneralPickupTimeCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const pickupTime = params.pickupTimeStr || (params.pickupDatetime ? formatTimeCell(parseInt(params.pickupDatetime, 10)) : '--');
    const pickupDate = params.pickupDateStr || (params.pickupDatetime ? getDate(parseInt(params.pickupDatetime, 10)) : '--');
    eDiv.innerHTML = `
      <div class="booking-grid-table">
        <div class="pickup-time">${pickupTime}</div>
        <div class="pickup-date">${pickupDate}</div>
      </div>`;
    return eDiv;
  };

  getGeneralPickupDropCell = (params: any): HTMLElement => {
    return this.commonBindingDataService.getPickDropLocation(params);
  };

  getGeneralDriverCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const driverName = params.driverName || 'NA';
    eDiv.innerHTML = `<div class="booking-grid-table"><div class="passenger-name">${driverName}</div></div>`;
    return eDiv;
  };

  getGeneralStatusCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const status = params.statusStr || 'NA';
    eDiv.innerHTML = `<div class="booking-grid-table"><div class="font-bold">${status}</div></div>`;
    return eDiv;
  };

  getDedicatedBookingIdCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const dayId = params.dedicatedTourDaywiseDataDtoList?.[0]?.daywiseDisplayTourId ?? 'NA';
    eDiv.innerHTML = `
      <div class="booking-grid-table" title="">
        <div class="flex">
          <span class="pickup-date font-normal m-0">Main ID</span>
          <div class="pickup-date pickup-date-only mt-0 opacity-100" title="${params.displayTourId}">&nbsp;-&nbsp;${params.displayTourId ?? 'NA'}</div>
        </div>
        <div class="flex drop-adr">
          <span class="pickup-date font-normal m-0">Day ID</span>
          <div class="pickup-date pickup-date-only mt-0 opacity-100" title="${dayId}">&nbsp;-&nbsp;${dayId}</div>
        </div>
      </div>`;
    return eDiv;
  };

  getDedicatedDurationCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const pickupDate = params.pickupDateTime ? getDate(parseInt(params.pickupDateTime, 10)) : (params.pickupDateTimeStr || 'NA');
    const endDate = params.endDateTime ? getDate(parseInt(params.endDateTime, 10)) : (params.endDateTimeStr || 'NA');
    eDiv.innerHTML = `
      <div class="booking-grid-table" title="">
        <div class="flex">
          <span class="pickup-date font-normal m-0">From</span>
          <div class="pickup-date pickup-date-only mt-0 opacity-100" title="${pickupDate}">-&nbsp;${pickupDate}</div>
        </div>
        <div class="flex drop-adr">
          <span class="pickup-date font-normal m-0">To</span>
          <div class="pickup-date pickup-date-only mt-0 opacity-100" title="${endDate}">-&nbsp;${endDate}</div>
        </div>
      </div>`;
    return eDiv;
  };

  getDedicatedPassengerCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const firstName = params.passengerFirstName || '';
    const lastName = params.passengerLastName || '';
    const fullName = `${firstName} ${lastName}`.trim() || 'NA';
    const day = params.dedicatedTourDaywiseDataDtoList?.[0];
    const passCount = day?.totalNoOfPassengers || 1;
    const childSeats = day?.totalNoOfBabyseats || 0;
    const wheelchairs = day?.totalNoOfWheelchairs || 0;
    const babySeats = day?.totalNoOfBabyseats || 0;

    let infoString = `${passCount} Pass`;
    if (childSeats > 0) {
      infoString += ` | ${childSeats}C`;
    }
    if (wheelchairs > 0) {
      infoString += ` | ${wheelchairs}W`;
    }
    if (babySeats > 0) {
      infoString += ` | ${babySeats}B`;
    }

    eDiv.innerHTML = `
      <div class="booking-grid-table">
        <div class="passenger-name w-full flex gap-1">
          <div title="${fullName}">${fullName}</div>
        </div>
        <div class="pickup-date drop-adr pass-info font-medium opacity-100" title="${infoString}" style="color: #05b061;">${infoString}</div>
      </div>`;
    return eDiv;
  };

  getDedicatedPickupLocationCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const locationName = params.pickupLocationName || params.dedicatedTourDaywiseDataDtoList?.[0]?.pickupLocationDisplayName || 'NA';
    const pickupTimeStr = params.dedicatedTourDaywiseDataDtoList?.[0]?.pickupDateTimeStr
      || params.pickupDateTimeStr
      || 'NA';

    eDiv.innerHTML = `
      <div class="col-12 location-time-box completed p-2 pb-0 dedicated-location-cell">
        <div class="grid pb-0 flex-nowrap">
          <div class="">
            <i class="pi pi-map-marker mr-2 text-sm" style="margin-top: 2px;"></i>
            <div class="col-12 location-time-separator for-pickup"></div>
          </div>
          <div class="p-0 pb-3 w-full text-sm location-name-text" title="${locationName}"><span class="location-text-ellipsis">${locationName}</span></div>
        </div>
        <div class="grid -mt-1 flex-nowrap">
          <div class="pr-2 text-overflow-clip"><i class="pi pi-clock" style="font-size: 13px;"></i></div>
          <div class="w-full text-sm" title="${pickupTimeStr}"><span class="font-semibold">${pickupTimeStr}</span></div>
        </div>
      </div>`;
    return eDiv;
  };

  getDedicatedDriverVehicleCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const daywiseData = params.dedicatedTourDaywiseDataDtoList;

    if (daywiseData?.length > 0) {
      const firstDay = daywiseData[0];
      const firstDriverName = `${firstDay.driverFirstName || ''} ${firstDay.driverLastName || ''}`.trim();
      const vehicleCode = firstDay.vehicleCode || '';
      const vehicleMakeModel = firstDay.vehicleMakeModel || '';
      const vehicleModelName = firstDay.vehicleModelName || '';
      const additionalCount = daywiseData.length > 1 ? daywiseData.length - 1 : 0;

      if (firstDay.driverId) {
        const displayName = firstDriverName || 'Driver Assigned';
        const driverDisplayName = additionalCount > 0
          ? `${displayName}... <span class="driver-count">+${additionalCount}</span>`
          : displayName;
        const vehicleInfo = vehicleCode || vehicleMakeModel || vehicleModelName
          ? `${vehicleCode ? vehicleCode + ' | ' : ''}${vehicleMakeModel ? vehicleMakeModel + ' ' : ''}${vehicleModelName || ''}`
          : '';

        eDiv.innerHTML = `
          <div class="booking-grid-table driver-vehicle-cell">
            <div class="passenger-name flex align-items-center" title="${displayName}${additionalCount > 0 ? ' +' + additionalCount + ' more' : ''}">
              <span class="driver-display-name">${driverDisplayName}</span>
            </div>
            ${vehicleInfo ? `<div class="pickup-date drop-adr pass-info vehicle" title="${vehicleInfo}">${vehicleInfo}</div>` : ''}
          </div>`;
      } else {
        eDiv.innerHTML = `
          <div class="booking-grid-table driver-vehicle-cell">
            <div class="passenger-name text-muted cursor-auto">NA</div>
          </div>`;
      }
    } else {
      eDiv.innerHTML = `
        <div class="booking-grid-table driver-vehicle-cell">
          <div class="passenger-name text-muted cursor-auto">NA</div>
        </div>`;
    }
    return eDiv;
  };

  getDedicatedStatusCell = (params: any): HTMLElement => {
    const eDiv = document.createElement('div');
    const bookingStatus = params.tourStatusStr || 'NA';
    const bookingStatusClass = this.commonBindingDataService.getDedicatedBookingStatusClass(params.tourStatus);
    const daywiseTourStatusStr = params.daywiseTourStatusStr
      ? `<div class="text-sm mt-2" title="Daywise Tour Status: ${params.daywiseTourStatusStr}">${params.daywiseTourStatusStr}</div>`
      : '';

    eDiv.innerHTML = `
      <div class="booking-grid-table" title="${bookingStatus}">
        <div class="font-bold text-base ${bookingStatusClass}" title="Booking Status: ${bookingStatus}">${bookingStatus}</div>
        ${daywiseTourStatusStr}
      </div>`;
    return eDiv;
  };

  onGeneralRowClicked(row: any) {
    this.router.navigate(['bookings/booking-details'], {
      queryParams: { id: row.id, navigateFrom: 'inboundList' }
    });
  }

  onDedicatedRowClicked(row: any) {
    this.router.navigate(['bookings/dedicated-bookings/booking-details/booking-info'], {
      queryParams: {
        id: row.id,
        navigateFrom: 'dedicated-bookings'
      }
    });
  }

  ngOnDestroy() {
    if (this._broadSub) {
      this._broadSub.unsubscribe();
    }
  }
}
